import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  Breadcrumbs,
  Link as MuiLink,
  Button,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { motion } from 'framer-motion';
import { useParams, useNavigate, Link } from 'react-router-dom';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import QuizIcon from '@mui/icons-material/Quiz';
import StyleIcon from '@mui/icons-material/Style';
import CodeIcon from '@mui/icons-material/Code';
import { fetchChapter } from '../utils/chapterApi';

// Import components
import ChapterContent from './ChapterContent';
import SecurePDFViewer from './SecurePDFViewer';
import ChapterSlideshow from './ChapterSlideshow';

// Course metadata
const courseMeta = {
  'binary-search': { name: 'Binary Search', icon: '🔍', premium: false },
  'arrays-and-hashing': { name: 'Arrays & Hashing', icon: '📊', premium: true },
  'two-pointer': { name: 'Two Pointer', icon: '👆', premium: true },
  'sliding-window': { name: 'Sliding Window', icon: '🪟', premium: true },
  'linked-list': { name: 'Linked Lists', icon: '🔗', premium: true },
  'tree': { name: 'Trees', icon: '🌳', premium: true },
  'trie': { name: 'Trie', icon: '🔤', premium: true },
  'heap': { name: 'Heap', icon: '📚', premium: true },
  'backtracking': { name: 'Backtracking', icon: '🔙', premium: true },
  'graph': { name: 'Graph', icon: '🕸️', premium: true },
  'dynamic-programming': { name: 'Dynamic Programming', icon: '📈', premium: true },
  'greedy': { name: 'Greedy', icon: '🎯', premium: true },
  'interval': { name: 'Interval', icon: '📅', premium: true },
  'monotonic-stack': { name: 'Monotonic Stack', icon: '📏', premium: true },
  'palindrome': { name: 'Palindrome', icon: '🔄', premium: true },
  'divide-and-conquer': { name: 'Divide & Conquer', icon: '✂️', premium: true },
  'prefix-sum': { name: 'Prefix Sum', icon: '➕', premium: true },
  'sorted-list': { name: 'SortedList', icon: '📋', premium: true },
  'segment-tree': { name: 'Segment Tree', icon: '🌲', premium: true },
  'parenthesis': { name: 'Parenthesis', icon: '( )', premium: true },
};

// PDF filename mapping for each course
const coursePdfFiles = {
  'binary-search': 'binary-search.pdf',
  'arrays-and-hashing': 'arrays-and-hashing.pdf',
  'two-pointer': 'two-pointer.pdf',
  'sliding-window': 'sliding-window.pdf',
  'linked-list': 'linked-list.pdf',
  'tree': 'tree.pdf',
  'trie': 'trie.pdf',
  'heap': 'heap.pdf',
  'backtracking': 'backtracking.pdf',
  'graph': 'graph.pdf',
  'dynamic-programming': 'dynamic-programming.pdf',
  'greedy': 'greedy.pdf',
  'interval': 'interval.pdf',
  'monotonic-stack': 'monotonic-stack.pdf',
  'palindrome': 'palindrome.pdf',
  'divide-and-conquer': 'divide-and-conquer.pdf',
  'prefix-sum': 'prefix-sum.pdf',
  'sorted-list': 'sorted-list.pdf',
  'segment-tree': 'segment-tree.pdf',
  'parenthesis': 'parenthesis.pdf',
};

// Section sidebar component for structured courses
const SectionSidebar = ({ sections, selectedSectionId, onSelectSection, completedSections }) => {
  return (
    <Card
      sx={{
        position: 'sticky',
        top: 100,
      }}
    >
      <CardContent sx={{ p: 0 }}>
        <Typography
          sx={{
            color: 'text.primary',
            fontWeight: 850,
            p: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          Sections
        </Typography>
        <List sx={{ p: 0 }}>
          {sections.map((section, index) => (
            <ListItem key={section.id} disablePadding>
              <ListItemButton
                selected={selectedSectionId === section.id}
                onClick={() => onSelectSection(section.id)}
                sx={{
                  py: 1.5,
                  px: 2,
                  borderLeft: selectedSectionId === section.id
                    ? '3px solid'
                    : '3px solid transparent',
                  borderColor: selectedSectionId === section.id ? 'secondary.main' : 'transparent',
                  '&.Mui-selected': {
                    bgcolor: 'rgba(81, 104, 255, 0.08)',
                  },
                  '&:hover': {
                    bgcolor: 'action.hover',
                  },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                  <Typography
                    sx={{
                      color: 'text.secondary',
                      fontSize: '0.8rem',
                      minWidth: 24,
                    }}
                  >
                    {index + 1}
                  </Typography>
                  <ListItemText
                    primary={section.title}
                    primaryTypographyProps={{
                      sx: {
                        color: selectedSectionId === section.id ? 'secondary.main' : 'text.primary',
                        fontSize: '0.9rem',
                        fontWeight: selectedSectionId === section.id ? 800 : 500,
                      },
                    }}
                  />
                  {completedSections[section.id] && (
                    <CheckCircleIcon sx={{ color: '#15905f', fontSize: 18 }} />
                  )}
                </Box>
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </CardContent>
    </Card>
  );
};

// Single section content renderer
const SectionContent = ({ section }) => {
  if (!section) return null;

  // Import the section rendering logic from ChapterContent
  // This is a simplified version that renders a single section
  return (
    <ChapterContent
      chapter={{
        title: section.title,
        sections: [section],
      }}
      singleSection={true}
    />
  );
};

const ChapterCompletionPanel = ({ completedCount, totalSections }) => {
  const complete = totalSections > 0 && completedCount === totalSections;
  const actions = [
    { label: 'Practice problems', to: '/problems', icon: <CodeIcon /> },
    { label: 'Take quiz', to: '/quizzes', icon: <QuizIcon /> },
    { label: 'Review flashcards', to: '/flashcards', icon: <StyleIcon /> },
  ];

  return (
    <Card sx={{ mt: 3 }}>
      <CardContent sx={{ p: { xs: 3, md: 4 } }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={5}>
            <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 850 }}>
              Chapter finish line
            </Typography>
            <Typography variant="h5" sx={{ color: 'text.primary', fontWeight: 850, mt: 0.5 }}>
              {complete ? 'You finished the lesson pass.' : `${completedCount} of ${totalSections} sections complete`}
            </Typography>
            <Typography sx={{ color: 'text.secondary', lineHeight: 1.65, mt: 1 }}>
              {complete
                ? 'Now turn the concept into retention: solve a few problems, quiz the recognition signals, and review the flashcards before moving on.'
                : 'Use the slideshow as a quick preview, finish each section, then move into practice while the pattern is fresh.'}
            </Typography>
          </Grid>
          <Grid item xs={12} md={7}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 1.5 }}>
              {actions.map((action) => (
                <Button
                  key={action.label}
                  component={Link}
                  to={action.to}
                  variant={complete ? 'contained' : 'outlined'}
                  startIcon={action.icon}
                  sx={{ justifyContent: 'flex-start', px: 2.2 }}
                >
                  {action.label}
                </Button>
              ))}
            </Box>
            <Typography sx={{ color: 'text.secondary', fontSize: '0.86rem', mt: 2 }}>
              Recommended next step: solve 2 representative problems from this chapter before starting another course.
            </Typography>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

function Course() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const [selectedSectionId, setSelectedSectionId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [chapterData, setChapterData] = useState(null);
  const [error, setError] = useState(null);
  const [completedSections, setCompletedSections] = useState(() => {
    const saved = localStorage.getItem(`completedSections_${courseId}`);
    return saved ? JSON.parse(saved) : {};
  });
  const [viewMode, setViewMode] = useState('lessons'); // 'lessons' or 'pdf'
  const [pdfLoading, setPdfLoading] = useState(false);

  const meta = courseMeta[courseId];
  const pdfFileName = coursePdfFiles[courseId];

  const handleViewModeChange = (event, newMode) => {
    if (newMode !== null) {
      setViewMode(newMode);
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [courseId]);

  // Fetch chapter data from API
  useEffect(() => {
    let cancelled = false;

    async function loadChapter() {
      setLoading(true);
      setError(null);
      setChapterData(null);
      setSelectedSectionId(null);

      try {
        const data = await fetchChapter(courseId);
        if (!cancelled) {
          setChapterData(data);
          // Set default section
          if (data?.sections?.length > 0) {
            setSelectedSectionId(data.sections[0].id);
          }
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    if (courseId && meta) {
      loadChapter();
    } else {
      setLoading(false);
    }

    return () => {
      cancelled = true;
    };
  }, [courseId, meta]);

  // Save completed sections
  useEffect(() => {
    localStorage.setItem(`completedSections_${courseId}`, JSON.stringify(completedSections));
  }, [completedSections, courseId]);

  if (!meta) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Typography sx={{ color: 'text.primary' }}>Course not found</Typography>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4, minHeight: 'calc(100vh - 120px)' }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress size={64} sx={{ color: 'secondary.main' }} />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ py: 4, minHeight: 'calc(100vh - 120px)' }}>
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h5" sx={{ color: 'text.primary', mb: 2 }}>
            {error === 'Subscription required' ? 'Premium Content' : 'Error Loading Course'}
          </Typography>
          <Typography sx={{ color: 'text.secondary', mb: 4 }}>
            {error === 'Subscription required'
              ? 'This course requires an active subscription to access.'
              : error}
          </Typography>
          {error === 'Subscription required' && (
            <Button
              variant="contained"
              onClick={() => navigate('/purchase')}
              sx={{
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                '&:hover': { bgcolor: 'primary.dark' },
              }}
            >
              View Subscription Options
            </Button>
          )}
        </Box>
      </Container>
    );
  }

  if (!chapterData) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Typography sx={{ color: 'text.primary' }}>No content available</Typography>
      </Container>
    );
  }

  const handleSectionSelect = (sectionId) => {
    const currentScrollY = window.scrollY;
    setSelectedSectionId(sectionId);
    window.requestAnimationFrame(() => {
      window.scrollTo({ top: currentScrollY, left: 0, behavior: 'auto' });
    });
  };

  const handleMarkComplete = () => {
    if (selectedSectionId) {
      setCompletedSections((prev) => ({
        ...prev,
        [selectedSectionId]: !prev[selectedSectionId],
      }));
    }
  };

  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
  };

  // Render structured course content
  if (chapterData?.sections) {
    const sections = chapterData.sections;
    const selectedSection = sections.find((s) => s.id === selectedSectionId);
    const completedCount = Object.values(completedSections).filter(Boolean).length;

    return (
      <Container maxWidth="xl" sx={{ py: { xs: 5, md: 7 }, minHeight: 'calc(100vh - 120px)' }}>
        <motion.div initial="hidden" animate="visible" variants={fadeInUp}>
          {/* Breadcrumb and header */}
          <Box sx={{ mb: 4 }}>
            <Breadcrumbs sx={{ mb: 2 }}>
              <MuiLink
                component={Link}
                to="/courses"
              sx={{ color: 'text.secondary', textDecoration: 'none', '&:hover': { color: 'secondary.main' } }}
              >
                Courses
              </MuiLink>
              <Typography sx={{ color: 'secondary.main', fontWeight: 700 }}>{meta.name}</Typography>
            </Breadcrumbs>

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box
                  sx={{
                    width: 58,
                    height: 58,
                    borderRadius: 4,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'rgba(81, 104, 255, 0.1)',
                    border: '1px solid',
                    borderColor: 'divider',
                    fontSize: '1.7rem',
                  }}
                >
                  {meta.icon}
                </Box>
                <Box>
                  <Typography variant="h3" sx={{ color: 'text.primary', fontWeight: 850, lineHeight: 1 }}>
                    {meta.name}
                  </Typography>
                  <Typography sx={{ color: 'text.secondary' }}>
                    {completedCount} / {sections.length} sections completed
                  </Typography>
                </Box>
              </Box>

              {/* View mode toggle */}
              {pdfFileName && (
                <ToggleButtonGroup
                  value={viewMode}
                  exclusive
                  onChange={handleViewModeChange}
                  size="small"
                  sx={{
                    bgcolor: 'background.paper',
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    '& .MuiToggleButton-root': {
                      color: 'text.secondary',
                      border: 'none',
                      px: 2,
                      py: 1,
                      '&.Mui-selected': {
                        bgcolor: 'primary.main',
                        color: 'primary.contrastText',
                        '&:hover': {
                          bgcolor: 'primary.main',
                        },
                      },
                      '&:hover': {
                        bgcolor: 'action.hover',
                      },
                    },
                  }}
                >
                  <ToggleButton value="lessons">
                    <MenuBookIcon sx={{ mr: 1, fontSize: 18 }} />
                    Lessons
                  </ToggleButton>
                  <ToggleButton value="pdf">
                    <PictureAsPdfIcon sx={{ mr: 1, fontSize: 18 }} />
                    PDF
                  </ToggleButton>
                </ToggleButtonGroup>
              )}
            </Box>
          </Box>

          {/* Main content - conditional based on viewMode */}
          {viewMode === 'lessons' ? (
            <>
              <ChapterSlideshow chapter={{ ...chapterData, id: courseId }} />
              <Grid container spacing={4}>
                {/* Sidebar */}
                <Grid item xs={12} md={3}>
                  <SectionSidebar
                    sections={sections}
                    selectedSectionId={selectedSectionId}
                    onSelectSection={handleSectionSelect}
                    completedSections={completedSections}
                  />
                </Grid>

                {/* Content area */}
                <Grid item xs={12} md={9}>
                {/* Mark complete card */}
                {selectedSection && (
                  <Card
                    onClick={handleMarkComplete}
                    sx={{
                      bgcolor: completedSections[selectedSectionId] ? 'rgba(21, 144, 95, 0.1)' : 'background.paper',
                      borderColor: completedSections[selectedSectionId] ? 'rgba(21, 144, 95, 0.25)' : 'divider',
                      mb: 3,
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                      },
                    }}
                  >
                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Checkbox
                          checked={!!completedSections[selectedSectionId]}
                          sx={{
                            color: 'secondary.main',
                            '&.Mui-checked': { color: '#15905f' },
                          }}
                        />
                        <Typography
                          sx={{
                            color: completedSections[selectedSectionId] ? '#15905f' : 'text.primary',
                            fontWeight: 800,
                          }}
                        >
                          {completedSections[selectedSectionId]
                            ? 'Section Completed!'
                            : `Mark "${selectedSection.title}" as Complete`}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                )}

                {/* Section content */}
                <Card
                  sx={{
                    bgcolor: 'background.paper',
                  }}
                >
                  <CardContent sx={{ p: 4 }}>
                    {selectedSection && <SectionContent section={selectedSection} />}
                  </CardContent>
                </Card>

                {/* Navigation buttons */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
                  {(() => {
                    const currentIndex = sections.findIndex((s) => s.id === selectedSectionId);
                    const prevSection = currentIndex > 0 ? sections[currentIndex - 1] : null;
                    const nextSection = currentIndex < sections.length - 1 ? sections[currentIndex + 1] : null;

                    return (
                      <>
                        <Box
                          onClick={() => prevSection && handleSectionSelect(prevSection.id)}
                          sx={{
                            cursor: prevSection ? 'pointer' : 'default',
                            opacity: prevSection ? 1 : 0.3,
                            color: 'text.secondary',
                            '&:hover': prevSection ? { color: 'secondary.main' } : {},
                          }}
                        >
                          {prevSection && `← ${prevSection.title}`}
                        </Box>
                        <Box
                          onClick={() => nextSection && handleSectionSelect(nextSection.id)}
                          sx={{
                            cursor: nextSection ? 'pointer' : 'default',
                            opacity: nextSection ? 1 : 0.3,
                            color: 'text.secondary',
                            '&:hover': nextSection ? { color: 'secondary.main' } : {},
                          }}
                        >
                          {nextSection && `${nextSection.title} →`}
                        </Box>
                      </>
                    );
                  })()}
                </Box>
                <ChapterCompletionPanel completedCount={completedCount} totalSections={sections.length} />
                </Grid>
              </Grid>
            </>
          ) : (
            /* PDF View */
            <Card
              sx={{
                overflow: 'hidden',
                position: 'relative',
              }}
            >
              {pdfLoading && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'rgba(0, 0, 0, 0.5)',
                    zIndex: 10,
                  }}
                >
                  <CircularProgress size={64} sx={{ color: 'secondary.main' }} />
                </Box>
              )}
              <SecurePDFViewer
                pdfFileName={pdfFileName}
                onLoadingChange={setPdfLoading}
              />
            </Card>
          )}
        </motion.div>
      </Container>
    );
  }

  // Fallback for unexpected cases
  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Typography sx={{ color: 'text.primary' }}>Unable to load course content</Typography>
    </Container>
  );
}

export default Course;
